<template>
  <div class="heatmap_coords">
    <template v-if="['click', 'tap', 'zoom', 'edit'].includes(eventType)">
      <div
        v-for="(item, index) in coords"
        :key="index"
        class="coords_rect"
        :class="{ active: item.selector === activeSelector, tracking_tag: item.isTrackingTag, edit: eventType === 'edit' }"
        :style="getCoordStyle(item)"
        @mouseenter="showCoordDetail(item, $event)"
        @mouseleave="hideCoordDetail(item)"
        @click="onCoordsClick(item)"
      >
        <transition name="coord-detail-fade">
          <div
            v-if="item.showDetail"
            class="coord_detail"
            :class="{ size_calculation: !item.isSizeCalculated }"
            :style="coordsLayerStyle"
          >
            <template v-if="eventType === 'click'">
              <div class="item_row_wrapper">
                <div class="item_row">
                  <span>{{ $t('common.unit.clicks') }}</span>
                  <span class="value_right">{{ item.clickCount | thousand }}</span>
                </div>
                <div class="item_row">
                  <span>{{ $t('common.reportIndicator.clickRate.label') }}</span>
                  <span class="value_right">{{ item.clickRate | fixed(1) }}%</span>
                </div>
              </div>
              <div class="item_row_wrapper hrline">
                <div class="item_row">
                  <span>{{ $t('common.reportIndicator.clicksPerPv.label') }}</span>
                  <span class="value_right">{{ item.clickPerPv | fixed(1) }}%</span>
                </div>
                <div class="item_row">
                  <span>{{ $t('common.reportIndicator.clickPvRate.label') }}</span>
                  <span class="value_right">{{ item.clickPvRate | fixed(1) }}%</span>
                </div>
              </div>
              <div
                v-if="device === 'desktop' && item.hoverClickRate !== undefined && item.hoverClickTime !== undefined && !isAMP"
                class="item_row_wrapper hrline"
              >
                <div class="item_row">
                  <span>
                    {{ $t('heatmap.coords.hover') }}
                    <i class="icon icon--heatmap-arrow"></i>
                    {{ $t('heatmap.coords.clickRate') }}
                  </span>
                  <span class="value_right">{{ item.hoverClickRate | fixed(1) }}%</span>
                </div>
                <div class="item_row">
                  <span>
                    {{ $t('heatmap.coords.hover') }}
                    <i class="icon icon--heatmap-arrow"></i>
                    {{ $t('heatmap.coords.clickTime') }}
                  </span>
                  <span class="value_right">{{ item.hoverClickTime | fixed(1) }}s</span>
                </div>
              </div>
              <i v-if="coordsLayerArrowData.isBottomLayout"
                class="arrow-up icon bg--box-arrow-up"
                :class="{ right: !coordsLayerArrowData.isLeftLayout }"></i>
              <i v-else
                class="arrow-down icon bg--box-arrow-down"
                :class="{ right: !coordsLayerArrowData.isLeftLayout }"></i>
            </template>

            <template v-else-if="eventType === 'tap'">
              <div class="item_row_wrapper">
                <div class="item_row">
                  <span>{{ $t('common.unit.tap') }}</span>
                  <span class="value_right">{{ item.tapCount | thousand }}</span>
                </div>
                <div class="item_row">
                  <span>{{ $t('common.reportIndicator.tapRate.label') }}</span>
                  <span class="value_right">{{ item.tapRate | fixed(1) }}%</span>
                </div>
              </div>
              <div class="item_row_wrapper hrline">
                <div class="item_row">
                  <span>{{ $t('common.reportIndicator.tapsPerPv.label') }}</span>
                  <span class="value_right">{{ item.tapPerPv | fixed(1) }}%</span>
                </div>
                <div class="item_row">
                  <span>{{ $t('common.reportIndicator.tapPvRate.label') }}</span>
                  <span class="value_right">{{ item.tapPvRate | fixed(1) }}%</span>
                </div>
              </div>
              <div class="item_row_wrapper hrline">
                <div class="item_row">
                  <span>{{ $t('common.reportIndicator.dblTapRate.label') }}</span>
                  <span class="value_right">{{ item.doubleTapRate | fixed(1) }}%</span>
                </div>
              </div>
            </template>

            <template v-else-if="eventType === 'zoom'">
              <div class="item_row_wrapper">
                <div class="item_row">
                  <span>{{ $t('common.unit.zoomin') }}</span>
                  <span class="value_right">{{ item.zoominCount | thousand }}</span>
                </div>
                <div class="item_row">
                  <span>{{ $t('common.reportIndicator.zoominRate.label') }}</span>
                  <span class="value_right">{{ item.zoominRate | fixed(1) }}%</span>
                </div>
              </div>
              <div class="item_row_wrapper hrline">
                <div class="item_row">
                  <span>{{ $t('common.unit.zoomout') }}</span>
                  <span class="value_right">{{ item.zoomoutCount | thousand }}</span>
                </div>
                <div class="item_row">
                  <span>{{ $t('common.reportIndicator.zoomoutRate.label') }}</span>
                  <span class="value_right">{{ item.zoomoutRate | fixed(1) }}%</span>
                </div>
              </div>
            </template>
          </div>
        </transition>
      </div>
    </template>
    <template v-if="ctaData && ctaData.coords">
      <div
        class="coords_rect coords_rect_except"
        :class="{ except: ctaData.except, disabled: isCtaCoordsDisabled }"
        :style="getCoordStyle(ctaData.coords)"
        @click="onCoordsClick(ctaData.coords)">
      </div>
      <div v-if="ctaData.except" class="except-border" :style="getCoordStyle(ctaData.coords)">
        <svg width="100%" height="100%">
          <line x1="0" y1="0" :x2="getCoordStyle(ctaData.coords).width"
            :y2="getCoordStyle(ctaData.coords).height"
            style="stroke:rgb(170,170,170); stroke-width:1" />
        </svg>
        <svg width="100%" height="100%">
          <line x1="0" :y1="getCoordStyle(ctaData.coords).height"
            :x2="getCoordStyle(ctaData.coords).width" y2="0"
            style="stroke:rgb(170,170,170); stroke-width:1" />
        </svg>
      </div>
      <div v-if="showCtaRectInfo"
        v-show="!isHeatmapDownloading"
        ref="ctaRectInfo"
        class="coords_rect_except_info"
        :style="ctaRectInfoData ? ctaRectInfoData.boxStyle : null">
        <p v-html="ctaCoordsInfo"></p>
        <i v-if="ctaRectInfoData && ctaRectInfoData.isBottomLayout"
          class="arrow-up icon bg--box-arrow-up"
          :class="{ right: ctaRectInfoData && !ctaRectInfoData.isLeftLayout }"></i>
        <i v-else
          class="arrow-down icon bg--box-arrow-down"
          :class="{ right: ctaRectInfoData && !ctaRectInfoData.isLeftLayout }"></i>
        <button type="button" class="close"
          @click="showCtaRectInfo = false">
          <i class="icon icon--close14"/>
        </button>
      </div>
    </template>

    <div v-if="crosshairData" class="crosshair">
      <div class="box" :style="crosshairData.boxStyle"></div>
      <div class="h_line" :style="{ top: crosshairData.center.top + 'px' }"></div>
      <div class="v_line" :style="{
        left: crosshairData.center.left + 'px',
        height: containerHeight + 'px'
      }"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HeatmapCoords',
  props: [
    'mode',
    'coordsData',
    'referer',
    'device',
    'eventType',
    'eventSource',
    'scale',
    'containerWidth',
    'containerHeight',
    'activeSelector',
    'ctaData',
    'isHeatmapDownloading',
    'isAMP',
    'coordsClick',
    'isDragging'
  ],
  data () {
    return {
      coords: [],
      coordsLayerStyle: null,
      coordsLayerArrowData: {},
      isLoadedOnce: false,
      showCtaRectInfo: true,
      ctaRectInfoData: null
    }
  },
  computed: {
    crosshairData () {
      if (!this.activeSelector) {
        return null
      }
      const coords = this.coordsData.coords[this.activeSelector]

      return {
        boxStyle: {
          width: (coords.width * this.scale) + 'px',
          height: (coords.height * this.scale) + 'px',
          left: (coords.left * this.scale) + 'px',
          top: (coords.top * this.scale) + 'px'
        },
        center: {
          left: (coords.left + (coords.width / 2)) * this.scale,
          top: (coords.top + (coords.height / 2)) * this.scale
        }
      }
    },
    isCtaCoordsDisabled () {
      if ((this.eventType === 'tap' || this.eventType === 'click') && this.ctaData && this.ctaData.coords) {
        for (const item of this.coords) {
          if (item.selector === this.ctaData.coords.selector) {
            return true
          }
        }
      }
      return false
    },
    ctaCoordsInfo () {
      if (!this.ctaData) {
        return null
      }
      if (this.ctaData.except) {
        return this.$t('cta.coordsInfoUnclicked')
      } else {
        return this.$t('cta.coordsInfoClicked')
      }
    }
  },
  watch: {
    scale (value) {
      this.clearCoords()
      this.$nextTick().then(this.loadCoords)
    },
    crosshairData (value) {
      if (value) {
        this.$emit('onHeatmapCenterChange', value.center)
      }
    },
    showCtaRectInfo: {
      handler (value) {
        if (value) {
          this.$nextTick().then(() => {
            this.setCtaRectInfoData()
          })
        } else {
          this.ctaRectInfoData = null
        }
      },
      immediate: true
    },
    containerWidth: {
      handler (value) {
        if (value) {
          this.$nextTick().then(() => {
            this.setCtaRectInfoData()
          })
        } else {
          this.ctaRectInfoData = null
        }
      },
      immediate: true
    }
  },
  mounted () {
    if (!this.isLoadedOnce) {
      this.loadCoords()
    }
  },
  methods: {
    clearCoords () {
      this.coords = []
    },
    loadCoords () {
      if (!this.coordsData) {
        return
      }
      this.coords = this.coordsData.coordsIds.map(selector => {
        return this.coordsData.coords[selector]
      })
    },
    getCoordStyle (item) {
      const style = {
        top: (item.top * this.scale) + 'px',
        left: (item.left * this.scale) + 'px',
        width: (item.width * this.scale) + 'px',
        height: (item.height * this.scale) + 'px'
      }

      if (this.mode === 'cta') {
        style.cursor = 'pointer'
      }
      return style
    },
    showCoordDetail (item, event) {
      if (this.isDragging) return
      if (this.ctaData && this.ctaData.coords && this.showCtaRectInfo) {
        return
      }
      if (this.eventType === 'edit') {
        return
      }
      const coordEl = event.target
      item.showDetail = true
      this.$emit('onToolTipHover', true)

      // opacity: 0 상태, 위치 계산 후 opacity 제거
      this.$nextTick().then(() => {
        const detailEl = coordEl.querySelector('.coord_detail')
        const layerWidth = detailEl.offsetWidth
        const layerHeight = detailEl.offsetHeight
        const coordsWidth = item.width * this.scale
        const coordsHeight = item.height * this.scale
        const coordsLeft = coordEl.offsetLeft
        const coordsTop = coordEl.offsetTop
        const HORIZONTAL_MARGIN = 24
        const BORDER_OFFSET = 2
        let isLeftLayout
        let isBottomLayout
        const layerStyle = {}

        if (this.device === 'phone') {
          if (coordsLeft + coordsWidth < (layerWidth + HORIZONTAL_MARGIN)) {
            isLeftLayout = true
          } else {
            isLeftLayout = false
          }
        } else {
          if (coordsLeft + layerWidth > (this.containerWidth - HORIZONTAL_MARGIN)) {
            isLeftLayout = false
          } else {
            isLeftLayout = true
          }
        }

        const ARROW_HEIGHT = 7

        if (coordsTop + coordsHeight + layerHeight + ARROW_HEIGHT > this.containerHeight) {
          isBottomLayout = false
        } else {
          isBottomLayout = true
        }

        if (isBottomLayout) {
          layerStyle.marginTop = coordsHeight + ARROW_HEIGHT + 'px'
        } else {
          layerStyle.marginTop = -(layerHeight + BORDER_OFFSET) - ARROW_HEIGHT + 'px'
        }

        if (!isLeftLayout) {
          layerStyle.left = 'auto'
          layerStyle.right = '-1px'
        }

        if (coordsWidth < 44) {
          const xOffset = Math.round((44 - coordsWidth) / 2) - 2

          if (isLeftLayout) {
            layerStyle.marginLeft = -xOffset + 'px'
          } else {
            layerStyle.marginRight = -xOffset + 'px'
          }
        }

        item.isSizeCalculated = true
        this.coordsLayerStyle = layerStyle
        this.coordsLayerArrowData = {
          isLeftLayout,
          isBottomLayout
        }
      })
    },
    hideCoordDetail (item) {
      item.showDetail = false
      item.isSizeCalculated = false
      if (!this.coordsClick) {
        this.$emit('onToolTipHover', false)
      }
      this.coordsLayerStyle = null
      this.coordsLayerArrowData = {}
    },
    onCoordsClick (coords) {
      if (this.mode === 'cta') {
        const selector = coords.selector
        const ctaData = { ...this.ctaData }

        if (ctaData.coords && ctaData.coords.selector === selector) {
          ctaData.except = !ctaData.except
        } else {
          ctaData.coords = {
            height: coords.height,
            left: coords.left,
            selector: coords.selector,
            top: coords.top,
            width: coords.width
          }
        }
        this.$emit('onCoordsClick', true)
        this.$emit('onCtaDataChange', ctaData)
        this.$emit('onToolTipHover', true)
      }
    },
    setCtaRectInfoData () {
      const coords = this.ctaData.coords

      if (!coords) {
        return null
      }
      const coordsWidth = coords.width * this.scale
      const coordsHeight = coords.height * this.scale
      const coordsLeft = coords.left * this.scale
      const coordsTop = coords.top * this.scale
      const boxWidth = this.$refs.ctaRectInfo.offsetWidth
      const boxHeight = this.$refs.ctaRectInfo.offsetHeight
      const result = {
        boxStyle: {
          opacity: 1
        },
        isLeftLayout: false,
        lsBottomLayout: false
      }

      const ARROW_HEIGHT = 7
      const isLeftLayout = coordsLeft + boxWidth <= this.containerWidth - 40
      const isBottomLayout = coordsTop + coordsHeight + boxHeight + ARROW_HEIGHT <= this.containerHeight

      if (isLeftLayout) {
        result.isLeftLayout = true
        result.boxStyle.left = `${coordsLeft}px`
      } else {
        result.boxStyle.right = `${this.containerWidth - (coordsLeft + coordsWidth)}px`
      }

      if (isBottomLayout) {
        result.isBottomLayout = true
        result.boxStyle.top = `${coordsTop + coordsHeight + ARROW_HEIGHT + 2}px`
      } else {
        result.boxStyle.top = `${coordsTop - ARROW_HEIGHT - 2 - boxHeight}px`
      }

      if (coordsWidth < 44) {
        const xOffset = Math.round((44 - coordsWidth) / 2) - 2

        if (isLeftLayout) {
          result.boxStyle.marginLeft = -xOffset + 'px'
        } else {
          result.boxStyle.marginRight = -xOffset + 'px'
        }
      }
      this.ctaRectInfoData = result
    }
  }
}
</script>

<style lang="scss">
.heatmap_coords {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 0;

  .arrow-up,
  .arrow-down {
    position: absolute;
    z-index: 20;
    top: -8px;
    left: 11px;

    &.right {
      left: auto;
      right: 11px;
    }
  }
  .arrow-down {
    top: auto;
    bottom: -8px;
  }

  .coords_rect {
    position: absolute;
    // transition: all 0.1s ease-in-out;
    border: dashed 1px #29e2ff;
    background-color: rgba(41, 226, 255, 0.1);
    box-sizing: border-box;

    &:hover {
      opacity: 1;
      border: solid 1px #29e2ff;
      background-color: rgba(41, 226, 255, 0.25);
    }

    &.active {
      border-color: transparent;
    }

    .coord_detail {
      position: absolute;
      z-index: 310;
      box-sizing: border-box;
      width: 191px;
      top: 0;
      left: -1px;
      padding: 5px 13px;
      white-space: nowrap;
      border-radius: 8px;
      pointer-events: none;
      box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
      background-color: #ffffff;
      font-size: 13px;
      color: #666666;
    }

    .coord_detail.size_calculation {
      opacity: 0;
      .arrow-up,
      .arrow-down {
        display: none;
      }
    }

    &.coords_rect_except {
      z-index: 300;
      border: 1px solid #ffe310;
      background-color: rgba(255, 227, 13, 0.3);
      &.except {
        pointer-events: auto;
        border-color: #ab85ff;
        background-color: rgba(171, 133, 255, 0.3);
        border: none;
        background: none;
      }
      &.disabled {
        pointer-events: none;
      }
    }

    &.tracking_tag {
      border-color: #ffe30d;
      background-color: rgba(255, 227, 13, 0.2);

      &:hover {
        border-color: #ffe30d;
        background-color: rgba(255, 227, 13, 0.25);
      }
    }

    &.edit {
      border: 0px;
      padding: 2px;
      background-color: rgba(255, 245, 0, 0.3);

      &:hover {
        border-color: #ffe30d;
        background-color: rgba(255, 245, 0, 0.3);
      }
    }
  }

  .except-border {
    position: absolute;
    border: 1px solid #aaa;
    box-sizing: border-box;
    svg {
      position: absolute;
      top: -1px;
      left: -1px;
    }
  }

  .coords_rect_except_info {
    white-space: nowrap;
    position: absolute;
    z-index: 300;
    line-height: 1.46;
    padding: 16px 20px 16px 16px;
    color: #444;
    box-sizing: border-box;
    border-radius: 8px;
    box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
    background-color: #ffffff;
    font-size: 13px;
    opacity: 0;

    .close {
      position: absolute;
      top: 8px;
      right: 8px;
      pointer-events: all;
    }
  }

  .coords_rect .item_row_wrapper {
    padding: 9px 0 8px 0;

    &.hrline {
      border-top: 1px solid #ebebeb;
    }

    .item_row {

      &:nth-last-of-type(1) {
        margin-top: 6px;
      }

      &::after {
        clear: both;
        display: block;
        content: "";
      }

      span {
        height: 13px;
        line-height: 1;
        display: inline-block;
        vertical-align: top;
      }

      .value_right {
        float: right;
        font-weight: 600;
      }
    }
  }
  .crosshair {
    pointer-events: none;
    .box {
      position: absolute;
      border: 1px solid #ffe30d;
      box-sizing: border-box;
    }

    .h_line,
    .v_line {
      position: absolute;
      background-color: #ffe30d;
    }

    .h_line {
      height: 1px;
      left: 0;
      right: 0;
    }

    .v_line {
      width: 1px;
      top: 0;
      bottom: 0;
    }
  }
}

.coord-detail-fade-enter-active, .coord-detail-fade-leave-active {
  transition: opacity .1s ease;
}
.coord-detail-fade-enter, .coord-detail-fade-leave-to {
  opacity: 0;
}
</style>
